import { Button } from '@artic/ui/button';
import { FullHeightChrome } from '@artic/ui/full-height-layout';
import { Skeleton } from '@artic/ui/skeleton';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';

import { projectBreadcrumbBadges } from '../../features/project-planning/project-route-chrome';
import { ProjectStepper } from '../../features/project-planning/project-stepper';
import { projectPlanningDetailQueryOptions } from '../../features/project-planning/queries';
import { deriveProductCode } from '../../features/run-sheets/derive-product-code';
import { RunSheetsWorkspace } from '../../features/run-sheets/run-sheets-workspace';

export const Route = createFileRoute('/projects/$projectId_/run_sheets')({
  staticData: {
    breadcrumb: (match, queryClient) => {
      const { projectId } = match.params as { projectId: string };
      const data = queryClient.getQueryData(projectPlanningDetailQueryOptions(projectId).queryKey);
      return data ? `${data.project_name} — Run Sheets` : 'Run Sheets';
    },
    breadcrumbBadges: projectBreadcrumbBadges,
    layout: 'full-height',
  },
  component: ProjectRunSheetsContent,
});

function ProjectRunSheetsContent() {
  const { projectId } = Route.useParams();

  const {
    data: project,
    isLoading,
    isError,
  } = useQuery({
    ...projectPlanningDetailQueryOptions(projectId),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 p-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-[60vh] w-full" />
      </div>
    );
  }

  if (isError || !project) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <h2 className="text-lg font-semibold">Error Loading Project Run Sheets</h2>
        <p className="text-muted-foreground text-sm">
          Could not load the project or its Run Sheets. Please try again.
        </p>
      </div>
    );
  }

  return (
    <RunSheetsWorkspace
      section="planner"
      scope={{ projectId, project }}
      topChrome={
        <FullHeightChrome>
          <ProjectStepper
            projectId={projectId}
            active="run_sheets"
            projectStatus={project.project_status}
            isProjectRedrafting={project.project_status === 'redrafting'}
          />
        </FullHeightChrome>
      }
      emptyState={
        <div className="flex flex-col items-center justify-center gap-2 py-20">
          <h2 className="text-lg font-semibold">No products in this project</h2>
          <p className="text-muted-foreground max-w-md text-center text-sm">
            Add work orders with product selections to the project and the Run Sheets will show the
            operations that apply to each product.
          </p>
          <Button variant="outline" asChild>
            <Link to="/projects/$projectId" params={{ projectId }}>
              Back to Project Planning
            </Link>
          </Button>
        </div>
      }
      renderProductLabel={(product, containers) => {
        // Pre-populate the work-orders grid's Product Code Set filter
        // with this product's derived code, so the user lands on a
        // pre-filtered view of just this product's work orders. The
        // stepper auto-labels the destination "Redrafting" when the
        // project has any redrafting work order, so the link is correct
        // for both the released and redrafting flows.
        const productCode = containers ? deriveProductCode(product, containers) : null;
        const showKeySubheading = product.product_key !== product.product_name;
        return (
          <Link
            to="/projects/$projectId"
            params={{ projectId }}
            {...(productCode
              ? {
                  search: {
                    filters: {
                      productCode: {
                        filterType: 'multi',
                        filterModels: [null, { filterType: 'set', values: [productCode] }],
                      },
                    },
                  },
                }
              : {})}
            className="flex min-w-0 flex-col leading-tight hover:underline focus-visible:underline"
            title={product.product_name}
          >
            <span className="truncate text-sm font-medium">{product.product_name}</span>
            {showKeySubheading && (
              <span className="text-muted-foreground truncate font-mono text-[10px]">
                {product.product_key}
              </span>
            )}
          </Link>
        );
      }}
    />
  );
}
